import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbPaginationModule, NgbTooltipModule, NgbAlertModule, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { ViewCourseModalComponent } from '../view-course-modal/view-course-modal.component';
import { CreateCourseModalComponent } from '../create-course-modal/create-course-modal.component';
import { EditCourseModalComponent } from '../edit-course-modal/edit-course-modal.component';
import { CourseService, CourseInformation, CourseFilters } from '../../services/course.service';
import { environment } from '../../../environments/environment';

interface CourseStatus {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/** แท็บกรองตามสถานะคอร์ส พร้อมจำนวนรายการในแต่ละสถานะ */
interface CourseStatusTab {
  /** '' = แท็บรวมทุกสถานะ */
  id: number | '';
  name: string;
  /** ชื่อสีของ bootstrap ที่ใช้ทั้งกับแท็บและ badge ในตาราง */
  color: string;
  count: number;
}

@Component({
    selector: 'app-course-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NgbPaginationModule,
        NgbTooltipModule,
        NgbAlertModule,
        NgbDropdownModule,
        StaffHeaderComponent,
        StaffNavComponent
    ],
    templateUrl: './course-management.component.html',
    styleUrl: './course-management.component.scss'
})
export class CourseManagementComponent implements OnInit, OnDestroy {
    currentUser: AuthUser | null = null;
    isMobileMenuOpen = false;
    private userSubscription: Subscription | undefined;

    courses: CourseInformation[] = [];
    filteredCourses: CourseInformation[] = [];
    isLoading = false;
    error: string | null = null;
    showMockDataWarning = false;

    // Course status list
    courseStatusList: CourseStatus[] = [];

    /**
     * แท็บแยกตามสถานะคอร์ส สร้างจาก /course-status ที่โหลดมา จึงไม่มีการเพิ่ม
     * สถานะใหม่เองในหน้าเว็บ แท็บแรกคือ "ทั้งหมด" เสมอ
     */
    statusTabs: CourseStatusTab[] = [];

    /** สีของแต่ละสถานะ ใช้ร่วมกันระหว่างแท็บกับ badge ในตาราง */
    private readonly statusColors: Record<string, string> = {
        'เตรียมเปิดคอร์ส': 'secondary',
        'เปิดรับสมัคร': 'success',
        'ปิดรับสมัคร': 'warning',
        'ยกเลิกคอร์ส': 'danger'
    };

    // Filter properties
    filters: CourseFilters = {
        searchTerm: '',
        category: '',
        level: '',
        status: '',
        instructor: '',
        certificationBySEC: '',
        dateRange: ''
    };

    // Pagination (จะทำงานบนฝั่ง frontend)
    page = 1;
    pageSize = 10;
    readonly pageSizeOptions = [10, 20, 50, 100];
    collectionSize = 0;

    // Sorting
    sortField = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    private subscription = new Subscription();

    constructor(
        private courseService: CourseService,
        private authService: AuthService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private modalService: NgbModal,
        private http: HttpClient
    ) { }

    ngOnInit(): void {
        console.log('🔄 CourseManagementComponent ngOnInit เริ่มต้น');

        // Subscribe to auth user first
        this.userSubscription = this.authService.currentUser$.subscribe(user => {
            console.log('👤 Current user updated:', user);
            this.currentUser = user;
            this.cdr.detectChanges();
        });

        // Use NgZone to ensure operations run inside Angular zone
        this.ngZone.run(() => {
            setTimeout(() => {
                console.log('⏰ เริ่มโหลดข้อมูลหลักสูตรหลังจาก timeout (inside NgZone)');
                this.loadCourseStatusList();
                this.loadCourses();
            }, 100);
        });
    }

    ngOnDestroy(): void {
        console.log('🗑️ CourseManagementComponent ngOnDestroy');
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }
        this.subscription.unsubscribe();
    }

    onLogout(): void {
        this.authService.logout();
    }

    toggleMobileMenu(): void {
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpen = false;
    }

    onNavigate(route: string): void {
        this.closeMobileMenu();

        switch (route) {
            case 'users':
                this.router.navigate(['/staff/user-management']);
                break;
            case 'courses':
                // Already on course management page
                break;
            case 'reports':
                this.router.navigate(['/staff/dashboard']);
                break;
            case 'settings':
                this.router.navigate(['/staff/dashboard']);
                break;
            default:
                this.router.navigate(['/staff/dashboard']);
                break;
        }
    }

    /**
     * Load course status list from API
     */
    loadCourseStatusList(): void {
        const apiUrl = `${environment.apiUrl}/course-status`;
        
        console.log('🔄 Loading course status from:', apiUrl);
        
        this.http.get<CourseStatus[]>(apiUrl, { observe: 'response' }).subscribe({
            next: (response) => {
                console.log('📡 API Response Status:', response.status);
                console.log('📦 Response body:', response.body);
                
                if (response.body && Array.isArray(response.body)) {
                    this.courseStatusList = response.body;
                    this.recomputeStatusTabs();
                    console.log('✅ Course status list loaded:', this.courseStatusList.length, 'items');
                    console.log('📋 Status list:', this.courseStatusList);
                } else {
                    console.warn('⚠️ Response body is empty or not an array');
                    this.courseStatusList = [];
                    this.recomputeStatusTabs();
                }
                
                this.cdr.detectChanges();
            },
            error: (error: HttpErrorResponse) => {
                console.error('❌ Error loading course status:', error);
                console.log('Error status:', error.status);
                console.log('Error message:', error.message);
                
                this.cdr.detectChanges();
                
                if (error.status === 304) {
                    console.log('📦 Using cached data (304 Not Modified)');
                }
            }
        });
    }

    /**
     * Load all courses from the server
     */
    loadCourses(): void {
        console.log('🚀 เริ่มโหลดข้อมูลหลักสูตร...');

        this.isLoading = true;
        this.error = null;

        const sub = this.courseService.getAllCourses().subscribe({
            next: (courses: CourseInformation[]) => {
                this.ngZone.run(() => {
                    console.log('✅ โหลดข้อมูลหลักสูตรสำเร็จ:', courses.length, 'รายการ');
                    console.log('📊 ข้อมูลหลักสูตรที่ได้รับ:', courses);

                    if (courses && Array.isArray(courses)) {
                        this.courses = [...courses];
                        this.filteredCourses = [...this.courses]; // Show all courses initially
                        this.collectionSize = this.filteredCourses.length;
                        this.recomputeStatusTabs();
                        this.applySort(); // เรียงอัปเดตล่าสุดขึ้นบนสุดตั้งแต่โหลด

                        // Check if we're using mock data
                        if (courses.length > 0 && courses[0].id === '1') {
                            this.showMockDataWarning = true;
                        }
                    } else {
                        console.warn('⚠️ ข้อมูลหลักสูตรที่ได้รับไม่ถูกต้อง:', courses);
                        this.courses = [];
                        this.filteredCourses = [];
                        this.collectionSize = 0;
                        this.recomputeStatusTabs();
                    }

                    this.isLoading = false;
                    this.cdr.detectChanges();
                });
            },
            error: (error: any) => {
                this.ngZone.run(() => {
                    console.error('❌ เกิดข้อผิดพลาดในการโหลดหลักสูตร:', error);
                    this.error = error;
                    this.courses = [];
                    this.filteredCourses = [];
                    this.collectionSize = 0;
                    this.recomputeStatusTabs();
                    this.isLoading = false;
                    this.cdr.detectChanges();
                });
            }
        });

        this.subscription.add(sub);
    }

    /**
     * Apply filters to courses (client-side filtering)
     */
    applyFilters(): void {
        console.log('🔍 กำลังใช้ตัวกรอง:', this.filters);

        // Reset to first page when applying filters
        this.page = 1;

        // Filter courses on client side
        this.filteredCourses = this.courses.filter(course =>
            this.matchesNonStatusFilters(course) && this.matchesStatusFilter(course)
        );

        this.collectionSize = this.filteredCourses.length;
        this.recomputeStatusTabs();

        // จัดเรียง: ถ้ามีการกดหัวคอลัมน์ใช้ค่านั้น ไม่งั้น default = อัปเดตล่าสุดอยู่บนสุด
        this.applySort();
    }

    /**
     * เงื่อนไขกรองทุกข้อ "ยกเว้น" สถานะ แยกออกมาเพื่อให้นับจำนวนในแต่ละแท็บได้
     * โดยยังเคารพตัวกรองอื่นที่ผู้ใช้เลือกไว้
     */
    private matchesNonStatusFilters(course: CourseInformation): boolean {
        {
            // Search term filter (title, description, instructor)
            if (this.filters.searchTerm) {
                const searchTerm = this.filters.searchTerm.toLowerCase();
                const titleMatch = course.title?.toLowerCase().includes(searchTerm);
                const descMatch = course.description?.toLowerCase().includes(searchTerm);
                const instructorMatch = course.instructor?.toLowerCase().includes(searchTerm);

                if (!titleMatch && !descMatch && !instructorMatch) {
                    return false;
                }
            }

            // Category filter
            if (this.filters.category && course.category !== this.filters.category) {
                return false;
            }

            // Level filter
            if (this.filters.level && course.level !== this.filters.level) {
                return false;
            }

            // Instructor filter
            if (this.filters.instructor) {
                const instructorFilter = this.filters.instructor.toLowerCase();
                if (!course.instructor?.toLowerCase().includes(instructorFilter)) {
                    return false;
                }
            }

            // รับรองโดย กลต. (certificationBySEC) filter
            if (this.filters.certificationBySEC) {
                const want = this.filters.certificationBySEC === 'true';
                if ((course.certificationBySEC === true) !== want) {
                    return false;
                }
            }

            // ช่วงเวลาอบรม/สัมมนา — กรองจากวันที่เริ่มอบรม (startDate) ย้อนหลังตามช่วงที่เลือก
            if (this.filters.dateRange) {
                if (!this.isWithinDateRange(course.startDate, this.filters.dateRange)) {
                    return false;
                }
            }

            return true;
        }
    }

    /** กรองตามแท็บสถานะที่เลือกอยู่ ('' = ทั้งหมด) */
    private matchesStatusFilter(course: CourseInformation): boolean {
        if (!this.filters.status) return true;
        return course.courseStatusId === Number(this.filters.status);
    }

    /**
     * สร้างแท็บสถานะใหม่พร้อมจำนวนในแต่ละแท็บ
     * จำนวนนับจากรายการที่ผ่านตัวกรองอื่นแล้ว (คำค้น, กลต., ช่วงเวลา)
     * ผู้ใช้จึงเห็นว่าถ้ากดแท็บนั้นจะได้กี่รายการจริง ๆ
     */
    private recomputeStatusTabs(): void {
        const matches = this.courses.filter(course => this.matchesNonStatusFilters(course));

        this.statusTabs = [
            { id: '', name: 'ทั้งหมด', color: 'dark', count: matches.length },
            ...this.courseStatusList.map(status => ({
                id: status.id,
                name: status.name,
                color: this.getStatusColor(status.name),
                count: matches.filter(course => course.courseStatusId === status.id).length
            }))
        ];
    }

    /** เลือกแท็บสถานะ */
    selectStatusTab(tab: CourseStatusTab): void {
        const nextStatus = tab.id === '' ? '' : String(tab.id);
        if (this.filters.status === nextStatus) return;

        this.filters.status = nextStatus;
        this.applyFilters();
    }

    isActiveStatusTab(tab: CourseStatusTab): boolean {
        const current = this.filters.status || '';
        return current === (tab.id === '' ? '' : String(tab.id));
    }

    private getStatusColor(statusName?: string): string {
        return this.statusColors[statusName || ''] || 'secondary';
    }

    /**
     * ตรวจว่า startDate อยู่ในช่วงย้อนหลังที่เลือกหรือไม่
     * '3m' = 3 เดือน, '6m' = 6 เดือน, '1y' = 1 ปี, '3y' = ไม่เกิน 3 ปี
     */
    private isWithinDateRange(startDate: string | undefined, range: string): boolean {
        if (!startDate) return false;
        const d = new Date(startDate);
        if (isNaN(d.getTime())) return false;
        const now = new Date();
        const from = new Date();
        switch (range) {
            case '3m': from.setMonth(from.getMonth() - 3); break;
            case '6m': from.setMonth(from.getMonth() - 6); break;
            case '1y': from.setFullYear(from.getFullYear() - 1); break;
            case '3y': from.setFullYear(from.getFullYear() - 3); break;
            default: return true;
        }
        return d >= from && d <= now;
    }

    /** จัดเรียงตามค่าที่เลือก หรือ default = updatedAt ล่าสุดอยู่บนสุด */
    private applySort(): void {
        if (this.sortField) {
            this.sortCourses();
        } else {
            this.filteredCourses.sort((a, b) => {
                const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return tb - ta;
            });
        }
    }

    /**
     * Clear all filters
     */
    clearFilters(): void {
        console.log('🔄 ล้างตัวกรองทั้งหมด');

        this.filters = {
            searchTerm: '',
            category: '',
            level: '',
            status: '',
            instructor: '',
            certificationBySEC: '',
            dateRange: ''
        };

        this.page = 1;
        this.filteredCourses = [...this.courses]; // Show all courses
        this.collectionSize = this.filteredCourses.length;
        this.recomputeStatusTabs();
        this.applySort();
    }

    /**
     * Handle page change (frontend pagination)
     */

    /**
     * เปลี่ยนจำนวนรายการต่อหน้า
     */
    changePageSize(size: number): void {
      this.pageSize = size;
      this.page = 1;
    }
    onPageChange(newPage: number): void {
        console.log('📄 เปลี่ยนหน้า:', newPage);
        this.page = newPage;
        // No need to call loadCourses() since we're doing frontend pagination
    }

    /**
     * Handle sort
     */
    onSort(field: string): void {
        console.log('🔄 จัดเรียงตาม:', field);

        if (this.sortField === field) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortField = field;
            this.sortDirection = 'asc';
        }

        // Apply sorting to current courses
        this.sortCourses();
    }

    /**
     * Sort courses locally
     */
    private sortCourses(): void {
        this.filteredCourses.sort((a, b) => {
            let valueA: any, valueB: any;

            switch (this.sortField) {
                case 'title':
                    valueA = a.title || '';
                    valueB = b.title || '';
                    break;
                case 'instructor':
                    valueA = a.instructor || '';
                    valueB = b.instructor || '';
                    break;
                case 'category':
                    valueA = a.category || '';
                    valueB = b.category || '';
                    break;
                case 'level':
                    valueA = a.level || '';
                    valueB = b.level || '';
                    break;
                case 'status':
                    valueA = a.status || '';
                    valueB = b.status || '';
                    break;
                case 'startDate':
                    valueA = new Date(a.startDate || 0);
                    valueB = new Date(b.startDate || 0);
                    break;
                case 'endDate':
                    valueA = new Date(a.endDate || 0);
                    valueB = new Date(b.endDate || 0);
                    break;
                case 'price':
                    valueA = a.price || 0;
                    valueB = b.price || 0;
                    break;
                case 'duration':
                    valueA = a.duration || 0;
                    valueB = b.duration || 0;
                    break;
                case 'currentStudents':
                    valueA = a.currentStudents || 0;
                    valueB = b.currentStudents || 0;
                    break;
                case 'regisSeat':
                    valueA = a.regisSeat || 0;
                    valueB = b.regisSeat || 0;
                    break;
                default:
                    return 0;
            }

            let comparison = 0;
            if (valueA > valueB) {
                comparison = 1;
            } else if (valueA < valueB) {
                comparison = -1;
            }

            return this.sortDirection === 'desc' ? -comparison : comparison;
        });
    }

    /**
     * Get sort icon for column
     */
    getSortIcon(field: string): string {
        if (this.sortField !== field) {
            return 'fas fa-sort text-muted';
        }
        return this.sortDirection === 'asc' ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
    }

    /**
     * View course details
     */
    viewCourse(course: CourseInformation): void {
        console.log('👁️ ดูรายละเอียดหลักสูตร:', course);
        
        // Navigate to view course page
        this.router.navigate(['/staff/course-management/view', course.id]);
    }

    /**
     * Edit course
     */
    editCourse(course: CourseInformation): void {
        console.log('✏️ แก้ไขหลักสูตร:', course);
        
        // Navigate to edit course page
        this.router.navigate(['/staff/course-management/edit', course.id]);
    }

    /**
     * Delete course
     */
    deleteCourse(course: CourseInformation): void {
        console.log('🗑️ ลบหลักสูตร:', course);
        // TODO: Implement course delete confirmation
    }

    /**
     * Format date for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return '-';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('th-TH', {
                // Course dates are naive wall-clock tagged UTC; format in UTC to avoid
                // the +7h (ICT) shift that rolls evening times to the next calendar day.
                timeZone: 'UTC',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return '-';
        }
    }

    /**
     * Format currency for display
     */
    formatCurrency(amount?: number): string {
        if (amount === undefined || amount === null) return '-';

        return new Intl.NumberFormat('th-TH', {
            style: 'currency',
            currency: 'THB'
        }).format(amount);
    }

    /**
     * รายได้รวม = จำนวนผู้เข้าอบรม (regisSeat) × ราคา
     * คอร์สที่ยังเตรียมเปิด/ยกเลิก จะไม่แสดงรายได้ (แสดง "–")
     */
    getCourseRevenue(course: CourseInformation): string {
        const statusName = course.courseStatus?.name || '';
        if (statusName.includes('เตรียม') || statusName.includes('ยกเลิก')) {
            return '–';
        }
        const revenue = (course.regisSeat || 0) * (course.price || 0);
        return this.formatCurrency(revenue);
    }

    /**
     * Get status badge class based on courseStatusId
     */
    getStatusBadgeClass(course: CourseInformation): string {
        return `badge bg-${this.getStatusColor(this.resolveStatusName(course))}`;
    }

    /**
     * ชื่อสถานะของคอร์ส อ่านจาก relation ก่อน ถ้าไม่มีค่อยเทียบจาก id กับรายการ
     * สถานะที่โหลดมาจาก API
     */
    private resolveStatusName(course: CourseInformation): string {
        return (
            course.courseStatus?.name ||
            this.courseStatusList.find(status => status.id === course.courseStatusId)?.name ||
            ''
        );
    }

    /**
     * Get level badge class
     */
    getLevelBadgeClass(level?: string): string {
        switch (level?.toLowerCase()) {
            case 'beginner':
                return 'badge bg-success';
            case 'intermediate':
                return 'badge bg-warning';
            case 'advanced':
                return 'badge bg-danger';
            default:
                return 'badge bg-secondary';
        }
    }

    /**
     * Track by course ID for ngFor performance
     */
    trackByCourseId(index: number, course: CourseInformation): string {
        return course.id || index.toString();
    }

    /**
     * Get paginated courses for current page
     */
    getPaginatedCourses(): CourseInformation[] {
        const startIndex = (this.page - 1) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.filteredCourses.slice(startIndex, endIndex);
    }

    /**
     * Handle dropdown open/close events
     */
    onDropdownOpenChange(isOpen: boolean, courseId?: string): void {
        if (isOpen) {
            console.log('📂 เปิด dropdown สำหรับหลักสูตร:', courseId);
        }
    }

    /**
     * Expose Math to template
     */
    Math = Math;

    /**
     * Open create course modal
     */
    openCreateCourseModal(): void {
        const modalRef = this.modalService.open(CreateCourseModalComponent, {
            size: 'lg',
            backdrop: 'static',
            keyboard: true,
            windowClass: 'create-course-modal',
            centered: true,
            scrollable: true
        });

        // Handle modal result
        modalRef.result.then(
            (result) => {
                if (result && result.success) {
                    console.log('✅ Course created successfully:', result.data);
                    // Reload courses after successful creation
                    this.loadCourses();
                    // Show success message (you can implement a toast/notification service)
                    alert('สร้างคอร์สอบรมสำเร็จ');
                }
            },
            (reason) => {
                // Modal dismissed
                console.log('❌ Create course modal dismissed:', reason);
            }
        );
    }
} 